import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Valida CPF usando o algoritmo dos dígitos verificadores
 */
function isValidCpf(cpf: string): boolean {
  // Remove caracteres não numéricos
  const cleaned = cpf.replace(/\D/g, '');

  // CPF deve ter 11 dígitos
  if (cleaned.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (ex: 111.111.111-11)
  if (/^(\d)\1{10}$/.test(cleaned)) {
    return false;
  }

  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  if (remainder !== parseInt(cleaned.charAt(9), 10)) {
    return false;
  }

  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  if (remainder !== parseInt(cleaned.charAt(10), 10)) {
    return false;
  }

  return true;
}

/**
 * Valida CNPJ usando o algoritmo dos dígitos verificadores
 */
function isValidCnpj(cnpj: string): boolean {
  // Remove caracteres não numéricos
  const cleaned = cnpj.replace(/\D/g, '');

  // CNPJ deve ter 14 dígitos
  if (cleaned.length !== 14) {
    return false;
  }

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(cleaned)) {
    return false;
  }

  // Validação do primeiro dígito verificador
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * weights1[i];
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== parseInt(cleaned.charAt(12), 10)) {
    return false;
  }

  // Validação do segundo dígito verificador
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * weights2[i];
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digit2 !== parseInt(cleaned.charAt(13), 10)) {
    return false;
  }

  return true;
}

/**
 * Validator para CPF - verifica formato e dígitos verificadores
 * Retorna erro { cpfInvalid: true } se inválido
 */
export function cpfValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null; // deixa Validators.required cuidar de campos obrigatórios
    }
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned.length === 0) {
      return null; // campo vazio
    }
    return isValidCpf(cleaned) ? null : { cpfInvalid: true };
  };
}

/**
 * Validator para CNPJ - verifica formato e dígitos verificadores
 * Retorna erro { cnpjInvalid: true } se inválido
 */
export function cnpjValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned.length === 0) {
      return null;
    }
    return isValidCnpj(cleaned) ? null : { cnpjInvalid: true };
  };
}

/**
 * Validator para CPF ou CNPJ - aceita ambos os formatos
 * Retorna erro { cpfCnpjInvalid: true } se inválido
 */
export function cpfCnpjValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned.length === 0) {
      return null;
    }
    
    // Verifica se é CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (cleaned.length === 11) {
      return isValidCpf(cleaned) ? null : { cpfCnpjInvalid: true };
    } else if (cleaned.length === 14) {
      return isValidCnpj(cleaned) ? null : { cpfCnpjInvalid: true };
    }
    
    // Quantidade de dígitos inválida
    return { cpfCnpjInvalid: true };
  };
}
